import { type ReactNode } from 'react';
import { Modal } from './modal';
import { Button } from './button';
import { Spinner } from './spinner';

/**
 * A reusable confirmation dialog for destructive or consequential actions.
 * Built on {@link Modal}: a short question with a cancel / confirm pair. Use
 * `tone="danger"` for irreversible or negative actions (e.g. rejecting).
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',
  pending = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'default' | 'danger';
  pending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={<h3 className="font-display text-base font-bold text-bone">{title}</h3>}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={pending}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            size="sm"
            onClick={onConfirm}
            disabled={pending}
          >
            {pending ? (
              <span className="inline-flex items-center gap-2">
                <Spinner size="sm" className="text-current" label="Working" />
                {confirmLabel}
              </span>
            ) : (
              confirmLabel
            )}
          </Button>
        </div>
      }
    >
      {description && <p className="text-sm leading-relaxed text-haze">{description}</p>}
    </Modal>
  );
}
